import { z } from 'zod'
import assert from 'assert'
import { decodePermissions } from '@erc725/erc725.js'
import { LSP6PermissionName } from '@lukso/lsp-smart-contracts'
import { ValueObject } from '@lukso/controllerapps-sharedlogic-ddd-core'
import { HexNumber } from '@lukso/controllerapps-sharedlogic-primitives'

import { ControlLevel } from './ControlLevel'

export type PermissionName = LSP6PermissionName

const accountInteractionsAndExtensionsPermissionNames: PermissionName[] = [
  'CALL',
  'SUPER_CALL',
  'DEPLOY',
  'REENTRANCY',
  'ADDEXTENSIONS',
  'CHANGEEXTENSIONS',
  'ADDUNIVERSALRECEIVERDELEGATE',
  'CHANGEUNIVERSALRECEIVERDELEGATE',
  'EXECUTE_RELAY_CALL',
  'STATICCALL',
  'SUPER_STATICCALL',
  'DELEGATECALL',
  'SUPER_DELEGATECALL',
  'TRANSFERVALUE',
  'SUPER_TRANSFERVALUE',
]

const administrationAndOwnershipPermissionNames: PermissionName[] = [
  'CHANGEOWNER',
  'EDITPERMISSIONS',
  'ADDCONTROLLER',
]

const profileDataAndSignPermissionNames: PermissionName[] = [
  'SETDATA',
  'SUPER_SETDATA',
  'SIGN',
  'ENCRYPT',
  'DECRYPT',
]

const PermissionsPropsSchema = z.object({
  permissionData: z.instanceof(HexNumber),
})

export type PermissionsProps = z.infer<typeof PermissionsPropsSchema>

export class Permissions extends ValueObject<PermissionsProps> {
  private readonly decodedPermissions: { [key in PermissionName]: boolean }

  constructor(props: PermissionsProps) {
    super(props)
    this.decodedPermissions = decodePermissions(props.permissionData.getValue())
  }

  /**
   * @returns the hex reprensentation of the permissions
   */
  public getPermissionData(): HexNumber {
    return this.props.permissionData
  }

  public getActivePermissions(): PermissionName[] {
    const allKeys = Object.keys(
      this.decodedPermissions
    ) as (keyof typeof this.decodedPermissions)[]
    return allKeys.filter(permissionName =>
      this.isPermissionEnabled(permissionName)
    )
  }

  private allPermissionsEnabled = (): boolean => {
    return Object.values(this.decodedPermissions).every(Boolean)
  }

  private somePermissionsEnabled = (): boolean => {
    return Object.values(this.decodedPermissions).some(Boolean)
  }

  private isPermissionEnabled = (permissionName: PermissionName): boolean => {
    return !!this.decodedPermissions[permissionName]
  }

  /**
   * @returns true if ANY of the profile data and sign permissions are enabled
   */
  public anyProfileDataAndSignEnabled = (): boolean => {
    return profileDataAndSignPermissionNames.some(permissionName =>
      this.isPermissionEnabled(permissionName)
    )
  }

  /**
   * @returns true if ANY of the account interactions and extensions permissions are enabled
   */
  public anyAccountInteractionsAndExtensionsEnabled = (): boolean => {
    return accountInteractionsAndExtensionsPermissionNames.some(
      permissionName => this.isPermissionEnabled(permissionName)
    )
  }

  /**
   * @returns true if ANY of the administration and ownership permissions are enabled
   */
  public anyAdministrationAndOwnershipEnabled = (): boolean => {
    return administrationAndOwnershipPermissionNames.some(permissionName =>
      this.isPermissionEnabled(permissionName)
    )
  }

  /**
   * @returns the control level of the permissions: Full, Partial or None
   */
  public getControlLevel(): ControlLevel {
    if (this.allPermissionsEnabled()) {
      return ControlLevel.Full
    }

    if (this.somePermissionsEnabled()) {
      return ControlLevel.Partial
    }

    return ControlLevel.None
  }

  public static create(props: PermissionsProps): Permissions {
    const validatedProps = PermissionsPropsSchema.parse(props)
    assert(
      validatedProps.permissionData.getByteLength() === 32,
      'permissionData must be 32 bytes long'
    )
    return new Permissions(validatedProps)
  }
}
